import React from 'react';

const RadarChart = ({ dataEsg = {
    policy: { companyOperation: 0, industrialOperation: 0, companyPotentialOperation: 0 },
    target: { companyOperation: 0, industrialOperation: 0, companyPotentialOperation: 0 },
    implementation: { companyOperation: 0, industrialOperation: 0, companyPotentialOperation: 0 },
    performance: { companyOperation: 0, industrialOperation: 0, companyPotentialOperation: 0 },
    disclosure: { companyOperation: 0, industrialOperation: 0, companyPotentialOperation: 0 }
} }) => {
    // Transform dataEsg into the required format with null checks
    const data = {
        Policy: dataEsg?.policy?.companyOperation ?? 0,
        Target: dataEsg?.target?.companyOperation ?? 0,
        Implementation: dataEsg?.implementation?.companyOperation ?? 0,
        Performance: dataEsg?.performance?.companyOperation ?? 0,
        Disclosure: dataEsg?.disclosure?.companyOperation ?? 0
    };

    const industrialAverage = {
        Policy: dataEsg?.policy?.industrialOperation ?? 0,
        Target: dataEsg?.target?.industrialOperation ?? 0,
        Implementation: dataEsg?.implementation?.industrialOperation ?? 0,
        Performance: dataEsg?.performance?.industrialOperation ?? 0,
        Disclosure: dataEsg?.disclosure?.industrialOperation ?? 0
    };

    const companyPotential = {
        Policy: dataEsg?.policy?.companyPotentialOperation ?? 0,
        Target: dataEsg?.target?.companyPotentialOperation ?? 0,
        Implementation: dataEsg?.implementation?.companyPotentialOperation ?? 0,
        Performance: dataEsg?.performance?.companyPotentialOperation ?? 0,
        Disclosure: dataEsg?.disclosure?.companyPotentialOperation ?? 0
    };

    const categories = Object.keys(data);
    const maxValue = 10; // Changed to 10 since the data is on a scale of 1-10
    const levels = 5;
    const centerX = 250;
    const centerY = 250;
    const radius = 200;


    const getPoint = (angle, value) => {
        const adjustedAngle = angle - Math.PI / 2; // Start from top
        const x = centerX + (radius * value / maxValue) * Math.cos(adjustedAngle);
        const y = centerY + (radius * value / maxValue) * Math.sin(adjustedAngle);
        return { x, y };
    };

    const generatePoints = (values) => {
        return categories.map((category, i) => {
            const angle = (i * 2 * Math.PI) / categories.length;
            return getPoint(angle, values[category]);
        });
    };

    const dataPoints = generatePoints(data);
    const averagePoints = generatePoints(industrialAverage);

    const gridLines = [...Array(levels)].map((_, i) => {
        const value = ((i + 1) * maxValue) / levels;
        const points = categories.map((_, j) => {
            const angle = (j * 2 * Math.PI) / categories.length;
            return getPoint(angle, value);
        });
        return points;
    });

    return (
        <div className="w-full h-[54rem] mx-auto p-6 bg-white rounded-xl shadow-lg flex flex-col justify-between">

            <div className='rounded-lg font-semibold'>
                Overview of the company's operations
                <p>in driving the organization towards sustainability.</p>

            </div>
            <div>

                <svg viewBox="0 0 500 500" className="w-full h-[24rem] p-2">
                    {gridLines.map((points, i) => (
                        <polygon
                            key={`grid-${i}`}
                            points={points.map(p => `${p.x},${p.y}`).join(' ')}
                            fill="none"
                            stroke="#3b3b40"
                            strokeWidth="0.8"
                            opacity="0.8"
                        />
                    ))}

                    {categories.map((_, i) => {
                        const angle = (i * 2 * Math.PI) / categories.length;
                        const point = getPoint(angle, maxValue);
                        return (
                            <line
                                key={`axis-${i}`}
                                x1={centerX}
                                y1={centerY}
                                x2={point.x}
                                y2={point.y}
                                stroke="#3b3b40"
                                strokeWidth="0.8"
                                opacity="1"
                            />
                        );
                    })}

                    <polygon
                        points={averagePoints.map(p => `${p.x},${p.y}`).join(' ')}
                        fill="#60A5FA"
                        fillOpacity="0" // fill color "0.2"
                        stroke="#60A5FA"
                        strokeWidth="2"
                        strokeDasharray="4"
                    />

                    <polygon
                        points={dataPoints.map(p => `${p.x},${p.y}`).join(' ')}
                        fill="#10B981"
                        fillOpacity="0.2"
                        stroke="#10B981"
                        strokeWidth="2"
                    />

                    <polygon
                        points={generatePoints(companyPotential).map(p => `${p.x},${p.y}`).join(' ')}
                        fill="#F59E0B"
                        fillOpacity="0"
                        stroke="#F59E0B"
                        strokeWidth="2"
                        strokeDasharray="4"
                    />

                    {categories.map((category, i) => {
                        const angle = (i * 2 * Math.PI) / categories.length;
                        const point = getPoint(angle, maxValue * 1.2);
                        return (
                            <text
                                key={`label-${i}`}
                                x={point.x}
                                y={point.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="text-[1.3rem] font-semibold fill-gray-700"
                            >
                                {category}
                            </text>
                        );
                    })}
                </svg>
                <div className="flex justify-center items-center gap-8">
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-0.5 bg-amber-500 border-dashed border-2"></div>
                        <span className="text-sm text-gray-600">Company's Potential Operation</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-0.5 bg-[#60A5FA] border-dashed border-2"></div>
                        <span className="text-sm text-gray-600">Industrial average</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-0.5 bg-emerald-500"></div>
                        <span className="text-sm text-gray-600">Company's Operation</span>
                    </div>                   
                </div>
            </div>


            <table className="w-full text-sm table-auto ">
                <tbody className="text-black">
                    <tr>
                        <td className="py-2 pr-4 font-semibold min-w-[6rem]">Policy</td>
                        <td className=' whitespace-pre-wrap break-words'>การจัดทำนโยบาย การระบุความมุ่งมั่น</td>
                    </tr>
                    <tr>
                        <td className="py-2 pr-4 font-semibold min-w-[6rem]">Target</td>
                        <td className=' whitespace-pre-wrap break-words'>การตั้งเป้าหมาย การกำหนดตัวชี้วัด การกำหนดกรอบระยะเวลาในการบรรลุผล</td>
                    </tr>
                    <tr>
                        <td className="py-2 pr-4 font-semibold min-w-[6rem] align-top">Implementation</td>
                        <td className=' whitespace-pre-wrap break-words'>การกำหนดกลยุทธ์เพื่อรองรับนโยบาย <br/> การมีแผนงาน/ขั้นตอนปฏิบัติงาน/กระบวนการ/แนวปฏิบัติอย่างชัดเจน <br/>
                            การกำหนดบุคคล/ผู้รับผิดชอบการปฏิบัติงานตามนโยบาย <br/> การรายงานผลไปยังคณะกรรมการ/ผู้บริหาร</td>
                    </tr>
                    <tr>
                        <td className="py-2 pr-4 font-semibold min-w-[6rem]">Performance</td>
                        <td className=' whitespace-pre-wrap break-words'>การวัดผลลัพธ์ที่เกิดขึ้นจากการดำเนินงาน การระบุประโยชน์เชิงปริมาณ</td>
                    </tr>
                    <tr>
                        <td className="py-2 pr-4 font-semibold min-w-[6rem]">Disclosure</td>
                        <td className=' whitespace-pre-wrap break-words'>การเปิดเผยข้อมูลสู่สาธารณะ</td>
                    </tr>
                   
                </tbody>
            </table>
        </div>
    );
};

export default RadarChart;
